import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import LogoIcon from '../../common/LogoIcon';
import { ncbService } from '../../services/ncbService';

const { 
  FiLayout, FiImage, FiMessageSquare, FiCalendar, FiLogOut, 
  FiPlus, FiTrash2, FiEdit2, FiSave, FiX, FiCheck, 
  FiUpload, FiGlobe, FiSettings, FiFileText, FiRefreshCw,
  FiLoader, FiInfo, FiStar, FiUser, FiArchive,
  FiUserCheck, FiShield, FiChevronDown, FiMail, FiPhone,
  FiEye, FiClock, FiCheckCircle, FiCornerUpRight, FiSend,
  FiExternalLink, FiFilter, FiHome, FiAlertCircle, FiHelpCircle,
  FiBriefcase, FiMapPin, FiInstagram, FiFacebook, FiDollarSign,
  FiTag, FiCoffee, FiUsers, FiSearch, FiVideo, FiPlay, FiRotateCw,
  FiChevronUp, FiCamera
} = FiIcons;

const Dashboard = () => {
  const navigate = useNavigate();
  const testimonialFileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Data States
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [rateSettings, setRateSettings] = useState(null);
  const [faqItems, setFaqItems] = useState([]);

  // UI States
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [openStatusDropdownId, setOpenStatusDropdownId] = useState(null);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [messageFilter, setMessageFilter] = useState('all');
  const [testimonialFilter, setTestimonialFilter] = useState('all');
  const [gallerySubTab, setGallerySubTab] = useState('photos');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Authentication Check
  useEffect(() => {
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    const hasToken = localStorage.getItem('admin_token') !== null;
    
    if (!isAdmin && !hasToken) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'bookings') {
        const data = await ncbService.getBookings();
        setBookings(Array.isArray(data) ? data : []);
      } else if (activeTab === 'messages') {
        const data = await ncbService.getContactMessages();
        setMessages(Array.isArray(data) ? data : []);
      } else if (activeTab === 'gallery') {
        const photos = await ncbService.getGalleryImages();
        const videos = await ncbService.getGalleryVideos();
        setGallery(Array.isArray(photos) ? photos.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)) : []);
        setGalleryVideos(Array.isArray(videos) ? videos.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)) : []);
      } else if (activeTab === 'testimonials') {
        const data = await ncbService.getTestimonials();
        setTestimonials(Array.isArray(data) ? data : []);
      } else if (activeTab === 'content') {
        const [profiles, rates, items] = await Promise.all([
          ncbService.getBusinessProfile(),
          ncbService.getRateSettings(),
          ncbService.getPageSectionItems()
        ]);
        setBusinessProfile(Array.isArray(profiles) && profiles.length > 0 ? profiles[0] : null);
        setRateSettings(Array.isArray(rates) ? rates.find(r => r.rate_key === 'standard_nightly_rate' && r.status === 'active') : null);
        setFaqItems(Array.isArray(items) ? items.filter(i => i.page_slug === 'home' && i.section_key === 'faq_items').sort((a, b) => (a.display_order || 0) - (b.display_order || 0)) : []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingStatusUpdate = async (id, newStatus) => {
    setActionLoading(true);
    try {
      await ncbService.updateBooking(id, { booking_status: newStatus });
      setOpenStatusDropdownId(null);
      fetchData();
    } catch (err) {
      alert(`Update failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenMessage = (msg) => {
    setSelectedItem(msg);
    setFormData(msg);
    setModalType('message_details');
    setShowModal(true);
  };

  const isDateRangeAvailable = (start, end, excludeId = null) => {
    if (!start || !end) return true;
    const startDt = new Date(start);
    const endDt = new Date(end);
    
    return !bookings.some(b => {
      if (excludeId && b.id === excludeId) return false;
      const bStatus = (b.booking_status || b.status || '').toLowerCase();
      if (bStatus === 'cancelled') return false;
      
      const bStart = new Date(b.start_date);
      const bEnd = new Date(b.end_date);
      
      return (startDt < bEnd && endDt > bStart);
    });
  };

  const calculateTotalAmount = (start, end) => {
    if (!start || !end || !rateSettings) return 0;
    const s = new Date(start);
    const e = new Date(end);
    const diffTime = e.getTime() - s.getTime();
    const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (nights <= 0) return 0;
    return nights * (rateSettings.rate_value || 0);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('folder', 'testimonials');

    try {
      const response = await fetch('/api/r2/upload', {
        method: 'POST',
        body: formDataUpload
      });

      if (!response.ok) throw new Error('Upload failed');
      
      const result = await response.json();
      if (result.ok && result.publicUrl) {
        setFormData(prev => ({ ...prev, profile_photo_url: result.publicUrl }));
      } else {
        throw new Error(result.message || result.error || 'Upload failed');
      }
    } catch (err) {
      alert(`Upload error: ${err.message}`);
    } finally {
      setUploadingPhoto(false);
      if (testimonialFileInputRef.current) testimonialFileInputRef.current.value = '';
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setActionLoading(true);
    try {
      if (modalType === 'testimonial') {
        const payload = {
          ...formData,
          display_order: parseInt(formData.display_order || 0),
          rating: parseInt(formData.rating || 5),
          is_featured: !!formData.is_featured,
          status: formData.status || 'published'
        };
        if (selectedItem) await ncbService.updateTestimonial(selectedItem.id, payload);
        else await ncbService.createTestimonial(payload);
      } else if (modalType === 'faq') {
        const payload = {
          item_title: formData.item_title,
          item_description: formData.item_description,
          display_order: parseInt(formData.display_order === "" ? (selectedItem?.display_order || 0) : (formData.display_order || 0)),
          status: formData.status || 'published',
          page_slug: 'home',
          section_key: 'faq_items'
        };
        if (selectedItem) await ncbService.updatePageSectionItem(selectedItem.id, payload);
        else await ncbService.createPageSectionItem(payload);
      } else if (modalType === 'business_profile') {
        if (selectedItem) await ncbService.updateBusinessProfile(selectedItem.id, formData);
        else await ncbService.createBusinessProfile(formData);
      } else if (modalType === 'rate_setting') {
        await ncbService.updateRateSetting(selectedItem.id, formData);
      } else if (modalType === 'message_details') {
        await ncbService.updateContactMessage(selectedItem.id, { 
          admin_notes: formData.admin_notes,
          status: formData.status || selectedItem.status
        });
      } else if (modalType === 'booking_form') {
        if (!isDateRangeAvailable(formData.start_date, formData.end_date, selectedItem?.id)) {
          throw new Error('The selected dates overlap with an existing booking.');
        }

        const emailVal = formData.guest_email ? formData.guest_email.trim() : '';
        const phoneVal = formData.guest_phone ? formData.guest_phone.trim() : '';
        let contactVal = `${emailVal} ${phoneVal}`.trim();
        
        if (!contactVal && formData.guest_contact) {
          contactVal = formData.guest_contact;
        }

        const payload = {
          guest_name: formData.guest_name,
          guest_contact: contactVal,
          start_date: formData.start_date,
          end_date: formData.end_date,
          total_guests: parseInt(formData.total_guests || 1, 10),
          total_amount: parseFloat(formData.total_amount || 0),
          booking_status: formData.booking_status || 'confirmed',
          notes: formData.notes || '',
          admin_notes: formData.admin_notes || '',
          source: formData.source || (selectedItem ? selectedItem.source : 'manual')
        };

        if (selectedItem) {
          await ncbService.updateBooking(selectedItem.id, payload);
        } else {
          payload.booking_title = `Direct Booking - ${formData.guest_name}`;
          await ncbService.createBooking(payload);
        }
      } else if (modalType === 'gallery_photo') {
        const payload = {
          ...formData,
          display_order: parseInt(formData.display_order || 0),
          status: formData.status || 'published'
        };
        if (selectedItem) await ncbService.updateGalleryImage(selectedItem.id, payload);
        else await ncbService.createGalleryImage(payload);
      } else if (modalType === 'gallery_video') {
        const payload = {
          ...formData,
          display_order: parseInt(formData.display_order || 0),
          status: formData.status || 'published',
          audio_mode: formData.audio_mode || 'muted'
        };
        if (selectedItem) await ncbService.updateGalleryVideo(selectedItem.id, payload);
        else await ncbService.createGalleryVideo(payload);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    setActionLoading(true);
    try {
      if (type === 'testimonial') await ncbService.deleteTestimonial(id);
      if (type === 'faq') await ncbService.deletePageSectionItem(id);
      if (type === 'booking') await ncbService.deleteBooking(id);
      if (type === 'gallery_photo') await ncbService.deleteGalleryImage(id);
      if (type === 'gallery_video') await ncbService.deleteGalleryVideo(id);
      fetchData();
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const renderStatusBadge = (status) => {
    const configs = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      confirmed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
      completed: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return (
      <span className={`px-2.5 py-1 text-[9px] font-black uppercase tracking-widest border rounded-full ${configs[status?.toLowerCase()] || 'bg-gray-100 text-gray-600'}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  const renderSourceBadge = (source) => {
    const configs = {
      website: { label: 'Website', styles: 'bg-blue-50 text-blue-600 border-blue-100' },
      manual: { label: 'Manual', styles: 'bg-gray-100 text-gray-600 border-gray-200' },
      airbnb_ical: { label: 'Airbnb iCal', styles: 'bg-rose-50 text-rose-600 border-rose-100' },
      vrbo_ical: { label: 'VRBO iCal', styles: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
      booking_ical: { label: 'Booking.com iCal', styles: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
      agoda_ical: { label: 'Agoda iCal', styles: 'bg-violet-50 text-violet-600 border-violet-100' }
    };
    
    const config = configs[source?.toLowerCase()] || { label: 'Unknown', styles: 'bg-gray-100 text-gray-400 border-gray-200' };
    
    return (
      <span className={`px-2 py-0.5 text-[8px] font-black uppercase tracking-widest border rounded-md inline-flex items-center gap-1.5 ${config.styles}`}>
        <div className={`w-1 h-1 rounded-full ${config.styles.split(' ')[1].replace('text-', 'bg-')}`} />
        {config.label}
      </span>
    );
  };

  const renderMessageStatusBadge = (status) => {
    const configs = {
      new: 'bg-blue-100 text-blue-700 border-blue-200',
      read: 'bg-gray-100 text-gray-700 border-gray-200',
      responded: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    const displayStatus = status || 'new';
    return (
      <span className={`px-2.5 py-1 text-[9px] font-black uppercase tracking-widest border rounded-full ${configs[displayStatus.toLowerCase()] || 'bg-gray-100 text-gray-600'}`}>
        {displayStatus}
      </span>
    );
  };

  const renderTestimonialStatusBadge = (status) => {
    const configs = {
      published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      inactive: 'bg-gray-100 text-gray-400 border-gray-200',
      draft: 'bg-amber-100 text-amber-700 border-amber-200'
    };
    const displayStatus = status || 'published';
    return (
      <span className={`px-2.5 py-1 text-[8px] font-black uppercase tracking-widest border rounded-full ${configs[displayStatus.toLowerCase()] || 'bg-gray-100 text-gray-600'}`}>
        {displayStatus}
      </span>
    );
  };

  const processedBookings = bookings
    .filter(b => {
      const status = b.booking_status || b.status;
      const matchesFilter = bookingFilter === 'all' || status?.toLowerCase() === bookingFilter.toLowerCase();
      const matchesSearch = !searchQuery || 
        b.guest_name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        b.guest_contact?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.id?.toString().includes(searchQuery);
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortBy === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortBy === 'stay_date') return new Date(a.start_date || 0) - new Date(b.start_date || 0);
      return 0;
    });

  const processedMessages = messages
    .filter(m => {
      const matchesSearch = !searchQuery || 
        m.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.message?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const status = m.status || 'new';
      let matchesFilter = true;
      if (messageFilter !== 'all') {
        matchesFilter = status.toLowerCase() === messageFilter.toLowerCase();
      }

      const dateStr = m.created_at || m.updated_at;
      const isValidDate = dateStr && 
                         !dateStr.includes('1970') && 
                         !isNaN(new Date(dateStr).getTime()) &&
                         new Date(dateStr).getTime() > 0;
      
      return matchesSearch && matchesFilter && isValidDate;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || a.updated_at || 0).getTime();
      const dateB = new Date(b.created_at || b.updated_at || 0).getTime();
      return dateB - dateA;
    });

  const processedTestimonials = testimonials
    .filter(t => {
      if (testimonialFilter === 'all') return true;
      return (t.status || 'published').toLowerCase() === testimonialFilter.toLowerCase();
    })
    .sort((a, b) => {
      const orderA = a.display_order ?? 999;
      const orderB = b.display_order ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      
      const dateA = new Date(a.created_at || a.updated_at || 0).getTime();
      const dateB = new Date(b.created_at || b.updated_at || 0).getTime();
      return dateB - dateA;
    });

  return (
    <div className="min-h-screen bg-brand-cream flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-brand-olive text-white p-6 flex flex-col shrink-0">
        <Link to="/" className="flex items-center gap-3 mb-10 hover:opacity-80 transition-all">
          <LogoIcon className="w-12 h-12 bg-white rounded-full p-2" />
          <div>
            <h1 className="font-serif text-lg tracking-widest leading-none">R. VENUE</h1>
            <p className="text-[10px] uppercase font-black tracking-[0.2em] text-brand-cream/60 mt-1">Admin</p>
          </div>
        </Link>
        <nav className="flex-grow space-y-2">
          {[
            { id: 'bookings', icon: FiCalendar, label: 'Bookings' }, 
            { id: 'messages', icon: FiMail, label: 'Message Center' },
            { id: 'gallery', icon: FiImage, label: 'Gallery' }, 
            { id: 'testimonials', icon: FiMessageSquare, label: 'Testimonials' }, 
            { id: 'content', icon: FiGlobe, label: 'Content Manager' },
            { id: 'ical', icon: FiRefreshCw, label: 'iCal Sync', isLink: true }
          ].map(item => (
            <button 
              key={item.id} 
              onClick={() => { 
                if (item.isLink) {
                  navigate('/admin/ical-sync');
                } else {
                  setActiveTab(item.id); setSearchQuery(''); setBookingFilter('all'); setMessageFilter('all'); setTestimonialFilter('all'); fetchData(); 
                }
              }} 
              className={`w-full flex items-center gap-3.5 px-5 py-3.5 transition-all text-left rounded-xl ${activeTab === item.id ? 'bg-white text-brand-olive font-bold shadow-md' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
            >
              <SafeIcon icon={item.icon} className="text-lg" />
              <span className="text-[11px] uppercase tracking-widest">{item.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className="mt-auto flex items-center gap-3 px-5 py-4 text-white/40 hover:text-white transition-all border-t border-white/10 pt-6">
          <SafeIcon icon={FiLogOut} className="text-lg" />
          <span className="text-[11px] uppercase tracking-widest font-black">Sign Out</span>
        </button>
      </aside>

      <main className="flex-grow p-6 md:p-10 overflow-y-auto max-h-screen">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h2 className="font-serif text-3xl text-brand-olive uppercase tracking-[0.1em]">{activeTab.replace('_', ' ')}</h2>
            <p className="text-brand-olive/60 text-[10px] font-black uppercase tracking-widest mt-1.5">Manage your website content and business flow</p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchData} className="p-3.5 text-brand-olive bg-white border border-brand-olive/10 hover:bg-brand-cream transition-colors rounded-full shadow-sm">
              <SafeIcon icon={FiRefreshCw} className={`text-lg ${loading ? 'animate-spin' : ''}`} />
            </button>
            {activeTab === 'bookings' && (
              <button 
                onClick={() => { 
                  setSelectedItem(null); 
                  setFormData({ 
                    booking_status: 'confirmed', 
                    total_guests: 1,
                    start_date: new Date().toISOString().split('T')[0],
                    end_date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
                    total_amount: rateSettings?.rate_value || 0,
                    isManualAmount: false,
                    source: 'manual'
                  }); 
                  setModalType('booking_form'); 
                  setShowModal(true); 
                }} 
                className="btn-primary flex items-center gap-2.5 px-5 py-3.5 text-[11px]"
              >
                <SafeIcon icon={FiPlus} className="text-base" /> Add Manual Booking
              </button>
            )}
            {activeTab === 'testimonials' && (
              <button 
                onClick={() => { setSelectedItem(null); setFormData({ rating: 5, status: 'published', display_order: 0, is_featured: false }); setModalType('testimonial'); setShowModal(true); }} 
                className="btn-primary flex items-center gap-2.5 px-5 py-3.5 text-[11px]"
              >
                <SafeIcon icon={FiPlus} className="text-base" /> Add Testimonial
              </button>
            )}
            {activeTab === 'gallery' && (
              <button 
                onClick={() => { 
                  setSelectedItem(null); 
                  setFormData({ status: 'published', display_order: 0 }); 
                  setModalType(gallerySubTab === 'photos' ? 'gallery_photo' : 'gallery_video'); 
                  setShowModal(true); 
                }} 
                className="btn-primary flex items-center gap-2.5 px-5 py-3.5 text-[11px]"
              >
                <SafeIcon icon={FiPlus} className="text-base" /> Add {gallerySubTab === 'photos' ? 'Photo' : 'Video'}
              </button>
            )}
          </div>
        </header>

        {loading ? (
          <div className="h-[60vh] flex flex-col items-center justify-center gap-4">
            <div className="w-12 h-12 border-3 border-brand-olive/20 border-t-brand-olive rounded-full animate-spin" />
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Loading records...</p>
          </div>
        ) : error ? (
          <div className="bg-white p-12 text-center border border-red-100 shadow-xl max-w-2xl mx-auto rounded-2xl">
            <SafeIcon icon={FiAlertCircle} className="text-red-400 text-3xl mx-auto mb-6" />
            <h3 className="font-serif text-xl text-brand-olive mb-3">Error Loading Data</h3>
            <p className="text-brand-olive/70 text-sm mb-8">{error}</p>
            <button onClick={fetchData} className="btn-outline px-6 py-3 text-[11px]">Try Again</button>
          </div>
        ) : (
          <div className="space-y-8">
            {activeTab === 'bookings' && (
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div className="flex flex-wrap gap-1.5 bg-white border border-brand-olive/10 rounded-full p-1 shadow-sm">
                    {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(status => (
                      <button 
                        key={status}
                        onClick={() => setBookingFilter(status)}
                        className={`px-4 py-2 text-[9px] font-black uppercase tracking-widest rounded-full transition-all ${bookingFilter === status ? 'bg-brand-olive text-white shadow-md' : 'text-brand-olive/40 hover:text-brand-olive'}`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-3 w-full md:w-auto">
                    <div className="relative flex-grow md:w-64">
                      <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-olive/30 text-base" />
                      <input 
                        type="text" 
                        placeholder="Search guests..." 
                        className="w-full pl-11 pr-5 py-3 bg-white border border-brand-olive/10 rounded-full text-xs font-bold uppercase tracking-widest outline-none focus:border-brand-olive transition-colors shadow-sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white shadow-xl border border-brand-olive/5 overflow-x-auto rounded-xl">
                  <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead>
                      <tr className="bg-brand-cream border-b border-brand-olive/10">
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Reference / Guest</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Stay Dates</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Amount</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Status</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-olive/5">
                      {processedBookings.map(booking => (
                        <tr key={booking.id} className="hover:bg-brand-cream/10 transition-colors">
                          <td className="p-6">
                            <div>
                              <div className="flex items-center gap-3 mb-1.5">
                                <p className="text-[10px] font-black text-brand-olive/40 uppercase tracking-widest">#{booking.id}</p>
                                {renderSourceBadge(booking.source)}
                              </div>
                              <p className="font-serif font-bold text-brand-olive text-base">{booking.guest_name}</p>
                              <p className="text-[11px] opacity-60 mt-0.5 font-medium">{booking.guest_contact}</p>
                            </div>
                          </td>
                          <td className="p-6 text-xs font-bold text-brand-olive/80">
                            {booking.start_date} — {booking.end_date}
                          </td>
                          <td className="p-6 text-sm font-bold text-brand-olive">
                            ₱{Number(booking.total_amount || 0).toLocaleString()}
                          </td>
                          <td className="p-6">
                            <div className="relative">
                              <button 
                                onClick={() => setOpenStatusDropdownId(openStatusDropdownId === booking.id ? null : booking.id)}
                                className="flex items-center gap-2.5 hover:opacity-70 transition-opacity"
                              >
                                {renderStatusBadge(booking.booking_status || booking.status)}
                                <SafeIcon icon={FiChevronDown} className="text-[10px] opacity-40" />
                              </button>
                              {openStatusDropdownId === booking.id && (
                                <div className="absolute left-0 mt-2.5 w-44 bg-white border border-brand-olive/10 shadow-2xl z-50 rounded-xl overflow-hidden">
                                  {['Pending', 'Confirmed', 'Completed', 'Cancelled'].map(status => (
                                    <button 
                                      key={status}
                                      onClick={() => handleBookingStatusUpdate(booking.id, status.toLowerCase())}
                                      className="w-full text-left px-4 py-3 text-[10px] uppercase font-bold hover:bg-brand-cream border-b border-brand-olive/5 last:border-0 transition-colors"
                                    >
                                      {status}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-6 text-right">
                            <div className="flex items-center justify-end gap-2.5">
                              <button 
                                onClick={() => { 
                                  setSelectedItem(booking); 
                                  let email = booking.guest_email || '';
                                  let phone = booking.guest_phone || '';
                                  if (!email && !phone && booking.guest_contact) {
                                    const parts = booking.guest_contact.split(' ');
                                    if (parts.length >= 2) {
                                      email = parts[0];
                                      phone = parts.slice(1).join(' ');
                                    } else {
                                      email = booking.guest_contact;
                                    }
                                  }
                                  setFormData({ 
                                    ...booking, 
                                    guest_email: email, 
                                    guest_phone: phone,
                                    isManualAmount: true 
                                  }); 
                                  setModalType('booking_form'); 
                                  setShowModal(true); 
                                }}
                                className="p-2.5 text-brand-olive hover:bg-brand-cream rounded-lg transition-colors border border-transparent hover:border-brand-olive/10"
                                title="Edit Booking"
                              >
                                <SafeIcon icon={FiEdit2} className="text-base" />
                              </button>
                              <button 
                                onClick={() => handleDelete(booking.id, 'booking')}
                                className="p-2.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
                                title="Delete Booking"
                              >
                                <SafeIcon icon={FiTrash2} className="text-base" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {processedBookings.length === 0 && <tr><td colSpan="5" className="p-16 text-center text-xs opacity-40 italic font-medium">No bookings found.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'messages' && (
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div className="flex flex-wrap gap-1.5 bg-white border border-brand-olive/10 rounded-full p-1 shadow-sm">
                    {[
                      { id: 'all', label: 'All Messages' },
                      { id: 'new', label: 'New' },
                      { id: 'read', label: 'Read' },
                      { id: 'responded', label: 'Responded' }
                    ].map(tab => (
                      <button 
                        key={tab.id}
                        onClick={() => setMessageFilter(tab.id)}
                        className={`px-4 py-2 text-[9px] font-black uppercase tracking-widest rounded-full transition-all ${messageFilter === tab.id ? 'bg-brand-olive text-white shadow-md' : 'text-brand-olive/40 hover:text-brand-olive'}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white shadow-xl border border-brand-olive/5 overflow-x-auto rounded-xl">
                  <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead>
                      <tr className="bg-brand-cream border-b border-brand-olive/10">
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Sender</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Subject & Message</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Received</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Status</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-olive/5">
                      {processedMessages.map(msg => {
                        const messageDate = new Date(msg.created_at || msg.updated_at);
                        return (
                          <tr key={msg.id} className={`hover:bg-brand-cream/10 transition-colors ${msg.status?.toLowerCase() === 'new' ? 'bg-brand-cream/5' : ''}`}>
                            <td className="p-6">
                              <div>
                                <p className="font-serif font-bold text-brand-olive text-base">{msg.name}</p>
                                <p className="text-[11px] opacity-60 mt-0.5 font-medium">{msg.email}</p>
                              </div>
                            </td>
                            <td className="p-6">
                              <div>
                                <p className="font-serif font-bold text-brand-olive text-sm line-clamp-1">{msg.subject || 'Inquiry'}</p>
                                <p className="text-xs text-brand-olive/70 mt-1 line-clamp-1 italic font-medium leading-relaxed">{msg.message}</p>
                              </div>
                            </td>
                            <td className="p-6">
                              <span className="text-xs font-bold text-brand-olive/80">
                                {messageDate.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            </td>
                            <td className="p-6">
                              {renderMessageStatusBadge(msg.status)}
                            </td>
                            <td className="p-6 text-right">
                              <button onClick={() => handleOpenMessage(msg)} className="p-2.5 text-brand-olive hover:bg-brand-cream rounded-lg transition-colors"><SafeIcon icon={FiEye} className="text-base" /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div className="space-y-8">
                <div className="flex flex-wrap gap-1.5 bg-white border border-brand-olive/10 rounded-full p-1 shadow-sm self-start inline-flex">
                  {[
                    { id: 'photos', label: 'Image Gallery / Photos', icon: FiCamera },
                    { id: 'videos', label: 'Reels / Videos', icon: FiVideo }
                  ].map(tab => (
                    <button 
                      key={tab.id}
                      onClick={() => setGallerySubTab(tab.id)}
                      className={`px-5 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-full transition-all flex items-center gap-2.5 ${gallerySubTab === tab.id ? 'bg-brand-olive text-white shadow-md' : 'text-brand-olive/40 hover:text-brand-olive'}`}
                    >
                      <SafeIcon icon={tab.icon} className="text-sm" />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {gallerySubTab === 'photos' ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                    {gallery.map(img => (
                      <div key={img.id} className="group bg-white border border-brand-olive/5 rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all">
                        <div className="aspect-square relative overflow-hidden">
                          <img src={img.image_url} className="w-full h-full object-cover" alt={img.alt_text} />
                          <div className="absolute inset-0 bg-brand-olive/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button onClick={() => { setSelectedItem(img); setFormData(img); setModalType('gallery_photo'); setShowModal(true); }} className="p-2 bg-white text-brand-olive rounded-full shadow-lg"><SafeIcon icon={FiEdit2} /></button>
                            <button onClick={() => handleDelete(img.id, 'gallery_photo')} className="p-2 bg-white text-red-500 rounded-full shadow-lg"><SafeIcon icon={FiTrash2} /></button>
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="text-[10px] font-black text-brand-olive/40 uppercase tracking-widest truncate">{img.caption || 'Untitled'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6">
                    {galleryVideos.map(vid => (
                      <div key={vid.id} className="group bg-white border border-brand-olive/5 rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all">
                        <div className="aspect-[9/16] relative overflow-hidden bg-brand-cream">
                          {vid.thumbnail_url && <img src={vid.thumbnail_url} className="w-full h-full object-cover" alt="" />}
                          <div className="absolute inset-0 bg-brand-olive/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button onClick={() => { setSelectedItem(vid); setFormData(vid); setModalType('gallery_video'); setShowModal(true); }} className="p-2 bg-white text-brand-olive rounded-full shadow-lg"><SafeIcon icon={FiEdit2} /></button>
                            <button onClick={() => handleDelete(vid.id, 'gallery_video')} className="p-2 bg-white text-red-500 rounded-full shadow-lg"><SafeIcon icon={FiTrash2} /></button>
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="text-[10px] font-black text-brand-olive/40 uppercase tracking-widest truncate">{vid.title || 'Untitled Reel'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'testimonials' && (
              <div className="bg-white shadow-xl border border-brand-olive/5 rounded-xl overflow-hidden">
                <table className="w-full text-left border-collapse table-auto min-w-[900px]">
                  <thead>
                    <tr className="bg-brand-cream border-b border-brand-olive/10">
                      <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60 w-16">Order</th>
                      <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Reviewer</th>
                      <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Testimonial</th>
                      <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Rating</th>
                      <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Status</th>
                      <th className="p-6 text-[10px] font-black uppercase tracking-widest text-brand-olive/60 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-olive/5">
                    {processedTestimonials.map(item => (
                      <tr key={item.id} className="hover:bg-brand-cream/10 transition-colors">
                        <td className="p-6 text-xs font-bold text-brand-olive/40">#{item.display_order || 0}</td>
                        <td className="p-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-brand-cream flex items-center justify-center text-brand-olive/40 border border-brand-olive/5">
                              {item.profile_photo_url ? <img src={item.profile_photo_url} className="w-full h-full rounded-full object-cover" /> : <SafeIcon icon={FiUser} />}
                            </div>
                            <div>
                              <p className="font-serif font-bold text-brand-olive text-sm">{item.reviewer_name}</p>
                              <p className="text-[9px] uppercase font-black opacity-40 tracking-widest">{item.reviewer_title}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-6">
                          <p className="text-xs text-brand-olive/70 line-clamp-2 max-w-xs font-medium italic">
                            {item.review_text ? `"${item.review_text}"` : <span className="opacity-40 font-normal">No testimonial text</span>}
                          </p>
                        </td>
                        <td className="p-6">
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <SafeIcon key={i} icon={FiStar} className={`text-[10px] ${i < (item.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
                            ))}
                          </div>
                        </td>
                        <td className="p-6">{renderTestimonialStatusBadge(item.status)}</td>
                        <td className="p-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => { setSelectedItem(item); setFormData(item); setModalType('testimonial'); setShowModal(true); }} className="p-2 text-brand-olive hover:bg-brand-cream rounded-lg"><SafeIcon icon={FiEdit2} /></button>
                            <button onClick={() => handleDelete(item.id, 'testimonial')} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><SafeIcon icon={FiTrash2} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'content' && (
              <div className="space-y-16">
                <section>
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="font-serif text-xl text-brand-olive uppercase tracking-widest">Business Profile</h3>
                    <button 
                      onClick={() => { setSelectedItem(businessProfile); setFormData(businessProfile || {}); setModalType('business_profile'); setShowModal(true); }}
                      className="p-2.5 text-brand-olive hover:bg-white bg-white/50 border border-brand-olive/10 rounded-lg transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest shadow-sm"
                    >
                      <SafeIcon icon={FiEdit2} /> Edit Profile
                    </button>
                  </div>
                  <div className="bg-white border border-brand-olive/10 rounded-2xl overflow-hidden shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 p-8">
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-2 block">Business Name</label>
                        <p className="font-serif text-lg text-brand-olive">{businessProfile?.business_name || 'Not Set'}</p>
                      </div>
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-2 block">Contact Email</label>
                        <p className="text-sm font-medium text-brand-olive/80">{businessProfile?.email || 'Not Set'}</p>
                      </div>
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-2 block">Phone Number</label>
                        <p className="text-sm font-medium text-brand-olive/80">{businessProfile?.phone || 'Not Set'}</p>
                      </div>
                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-2 block">Office Address</label>
                        <p className="text-sm font-medium text-brand-olive/80 leading-relaxed">{businessProfile?.address || 'Not Set'}</p>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="font-serif text-xl text-brand-olive uppercase tracking-widest">Rate Settings</h3>
                  </div>
                  <div className="bg-white border border-brand-olive/10 p-8 rounded-2xl shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 bg-brand-cream rounded-2xl flex items-center justify-center border border-brand-olive/5 shadow-inner">
                        <SafeIcon icon={FiDollarSign} className="text-2xl text-brand-olive" />
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 mb-1.5">Standard Nightly Rate</p>
                        <p className="font-serif text-3xl text-brand-olive">₱{Number(rateSettings?.rate_value || 0).toLocaleString()}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => { setSelectedItem(rateSettings); setFormData(rateSettings || {}); setModalType('rate_setting'); setShowModal(true); }}
                      className="btn-outline px-6 py-3 text-[10px]"
                    >
                      <SafeIcon icon={FiEdit2} className="mr-2" /> Update Rate
                    </button>
                  </div>
                </section>

                <section>
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="font-serif text-xl text-brand-olive uppercase tracking-widest">FAQ Manager</h3>
                  </div>
                  <div className="space-y-4">
                    {faqItems.map(faq => (
                      <div key={faq.id} className="bg-white border border-brand-olive/10 p-6 flex justify-between items-start rounded-xl">
                        <div className="max-w-2xl">
                          <p className="font-serif font-bold text-lg text-brand-olive mb-2">{faq.item_title}</p>
                          <p className="text-sm opacity-70 leading-relaxed font-medium">{faq.item_description}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button onClick={() => { setSelectedItem(faq); setFormData(faq); setModalType('faq'); setShowModal(true); }} className="p-2.5 text-brand-olive hover:bg-brand-cream rounded-lg"><SafeIcon icon={FiEdit2} className="text-base" /></button>
                          <button onClick={() => handleDelete(faq.id, 'faq')} className="p-2.5 text-red-500 hover:bg-red-50 rounded-lg"><SafeIcon icon={FiTrash2} className="text-base" /></button>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => { setSelectedItem(null); setFormData({ status: 'published', display_order: 0 }); setModalType('faq'); setShowModal(true); }} className="w-full py-4 border border-dashed border-brand-olive/20 bg-white text-brand-olive/60 text-[10px] font-black uppercase flex items-center justify-center gap-2.5 hover:bg-brand-cream transition-all rounded-xl tracking-widest"><SafeIcon icon={FiPlus} className="text-lg" /> Add FAQ Item</button>
                  </div>
                </section>
              </div>
            )}
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-brand-olive/70 backdrop-blur-md" onClick={() => setShowModal(false)}>
          <div className="bg-white w-full max-w-xl shadow-2xl overflow-hidden rounded-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-8 border-b border-brand-olive/10 flex items-center justify-between bg-brand-cream">
              <h3 className="font-serif text-xl text-brand-olive uppercase tracking-widest">{selectedItem ? 'Edit' : 'Add'} {modalType?.replace('_', ' ')}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-brand-olive/5 rounded-full transition-colors"><SafeIcon icon={FiX} className="text-2xl opacity-40" /></button>
            </div>
            <form onSubmit={handleSave} className="p-8 space-y-6 max-h-[75vh] overflow-y-auto">
              {modalType === 'booking_form' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-brand-cream/30 border border-brand-olive/5 rounded-xl">
                    <div className="space-y-0.5">
                      <label className="text-[9px] font-black uppercase opacity-40 tracking-widest">Booking Source</label>
                      <div className="flex items-center gap-2">
                        {renderSourceBadge(formData.source || 'manual')}
                        {formData.source && !['website', 'manual'].includes(formData.source.toLowerCase()) && (
                           <span className="text-[10px] text-brand-olive/40 italic font-medium">External Sync</span>
                        )}
                      </div>
                    </div>
                    {selectedItem && (
                      <div className="text-right space-y-0.5">
                        <label className="text-[9px] font-black uppercase opacity-40 tracking-widest">Booking ID</label>
                        <p className="text-[10px] font-black text-brand-olive/60">#{selectedItem.id}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Guest Name</label>
                      <input 
                        type="text" required 
                        className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                        value={formData.guest_name || ''} 
                        onChange={e => setFormData({...formData, guest_name: e.target.value})} 
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Number of Guests</label>
                      <input 
                        type="number" min="1" max="15" required
                        className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                        value={formData.total_guests || 1} 
                        onChange={e => setFormData({...formData, total_guests: e.target.value})} 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Guest Email</label>
                      <input 
                        type="email" 
                        className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                        value={formData.guest_email || ''} 
                        onChange={e => setFormData({...formData, guest_email: e.target.value})} 
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Guest Phone</label>
                      <input 
                        type="text" 
                        className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                        value={formData.guest_phone || ''} 
                        onChange={e => setFormData({...formData, guest_phone: e.target.value})} 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Check-in Date</label>
                      <input 
                        type="date" required 
                        className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                        value={formData.start_date || ''} 
                        onChange={e => {
                          const newStart = e.target.value;
                          const updates = { start_date: newStart };
                          if (!formData.isManualAmount) {
                            updates.total_amount = calculateTotalAmount(newStart, formData.end_date);
                          }
                          setFormData({...formData, ...updates});
                        }} 
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Check-out Date</label>
                      <input 
                        type="date" required 
                        className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                        value={formData.end_date || ''} 
                        onChange={e => {
                          const newEnd = e.target.value;
                          const updates = { end_date: newEnd };
                          if (!formData.isManualAmount) {
                            updates.total_amount = calculateTotalAmount(formData.start_date, newEnd);
                          }
                          setFormData({...formData, ...updates});
                        }} 
                      />
                    </div>
                  </div>

                  {!isDateRangeAvailable(formData.start_date, formData.end_date, selectedItem?.id) && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600">
                      <SafeIcon icon={FiAlertCircle} className="text-lg shrink-0" />
                      <p className="text-[11px] font-bold uppercase tracking-wider">Warning: Selected dates overlap with an existing booking.</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Booking Status</label>
                      <select 
                        className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                        value={formData.booking_status || 'confirmed'} 
                        onChange={e => setFormData({...formData, booking_status: e.target.value})}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Total Amount (PHP)</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-olive/40 font-bold">₱</span>
                        <input 
                          type="number" required 
                          className="w-full pl-8 pr-4 py-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                          value={formData.total_amount || 0} 
                          onChange={e => setFormData({...formData, total_amount: e.target.value, isManualAmount: true})} 
                        />
                      </div>
                      {formData.isManualAmount && (
                         <button 
                           type="button"
                           onClick={() => setFormData({
                             ...formData, 
                             total_amount: calculateTotalAmount(formData.start_date, formData.end_date),
                             isManualAmount: false
                           })}
                           className="text-[9px] text-brand-olive/60 hover:text-brand-olive underline mt-1"
                         >
                           Reset to suggested: ₱{calculateTotalAmount(formData.start_date, formData.end_date).toLocaleString()}
                         </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Guest Message / Special Requests</label>
                    <textarea 
                      rows="3" 
                      className="w-full p-4 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/10 text-sm rounded-xl font-medium" 
                      placeholder="Enter any special requests from the guest..."
                      value={formData.notes || ''} 
                      onChange={e => setFormData({...formData, notes: e.target.value})} 
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Internal Admin Notes</label>
                    <textarea 
                      rows="3" 
                      className="w-full p-4 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/10 text-sm rounded-xl font-medium shadow-inner" 
                      placeholder="Add your own internal notes here..."
                      value={formData.admin_notes || ''} 
                      onChange={e => setFormData({...formData, admin_notes: e.target.value})} 
                    />
                  </div>
                </div>
              )}
              {modalType === 'message_details' && (
                <div className="space-y-6">
                  <div className="bg-brand-cream/30 p-5 rounded-xl border border-brand-olive/5 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black uppercase opacity-40 tracking-widest block mb-1">Sender</label>
                        <p className="font-serif text-brand-olive text-lg">{selectedItem?.name}</p>
                      </div>
                      <div>
                        <label className="text-[10px] font-black uppercase opacity-40 tracking-widest block mb-1">Email</label>
                        <p className="text-xs font-bold text-brand-olive/70">{selectedItem?.email}</p>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest block mb-1">Subject</label>
                      <p className="text-sm font-bold text-brand-olive">{selectedItem?.subject || 'Direct Inquiry'}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest block mb-1">Message Content</label>
                      <p className="text-xs text-brand-olive/70 leading-relaxed font-medium bg-white/50 p-4 rounded-lg border border-brand-olive/5 italic">"{selectedItem?.message}"</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Message Status</label>
                      <select 
                        className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg font-bold" 
                        value={formData.status || 'new'} 
                        onChange={e => setFormData({...formData, status: e.target.value})}
                      >
                        <option value="new">New / Unread</option>
                        <option value="read">Read</option>
                        <option value="responded">Responded</option>
                      </select>
                    </div>
                    <div className="flex items-end pb-1">
                      <a 
                        href={`mailto:${selectedItem?.email}?subject=Re: ${selectedItem?.subject || 'Inquiry at R. Venue Apartelle'}`}
                        className="w-full bg-brand-olive text-white py-3 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-brand-olive/90 transition-all shadow-sm"
                      >
                        <SafeIcon icon={FiMail} /> Reply via Gmail
                      </a>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Internal Admin Notes</label>
                    <textarea 
                      rows="4" 
                      className="w-full p-4 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/10 text-sm rounded-xl font-medium" 
                      placeholder="Log your response or any internal follow-ups..."
                      value={formData.admin_notes || ''} 
                      onChange={e => setFormData({...formData, admin_notes: e.target.value})} 
                    />
                  </div>
                </div>
              )}
              {modalType === 'gallery_photo' && (
                <div className="space-y-4">
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Image URL</label><input type="url" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.image_url || ''} onChange={e => setFormData({...formData, image_url: e.target.value})} /></div>
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Caption / Title</label><input type="text" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.caption || ''} onChange={e => setFormData({...formData, caption: e.target.value})} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Display Order</label><input type="number" className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.display_order || 0} onChange={e => setFormData({...formData, display_order: e.target.value})} /></div>
                    <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Status</label><select className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.status || 'published'} onChange={e => setFormData({...formData, status: e.target.value})}><option value="published">Visible</option><option value="inactive">Hidden</option></select></div>
                  </div>
                </div>
              )}
              {modalType === 'gallery_video' && (
                <div className="space-y-4">
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Video URL</label><input type="url" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.video_url || ''} onChange={e => setFormData({...formData, video_url: e.target.value})} /></div>
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Thumbnail URL</label><input type="url" className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.thumbnail_url || ''} onChange={e => setFormData({...formData, thumbnail_url: e.target.value})} /></div>
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Title</label><input type="text" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Audio Mode</label><select className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.audio_mode || 'muted'} onChange={e => setFormData({...formData, audio_mode: e.target.value})}><option value="muted">Muted</option><option value="audio">Audio On</option></select></div>
                    <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Display Order</label><input type="number" className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.display_order || 0} onChange={e => setFormData({...formData, display_order: e.target.value})} /></div>
                  </div>
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Status</label><select className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.status || 'published'} onChange={e => setFormData({...formData, status: e.target.value})}><option value="published">Visible</option><option value="inactive">Hidden</option></select></div>
                </div>
              )}
              {modalType === 'faq' && (
                <div className="space-y-4">
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Question</label><input type="text" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.item_title || ''} onChange={e => setFormData({...formData, item_title: e.target.value})} /></div>
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Answer</label><textarea required rows="4" className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.item_description || ''} onChange={e => setFormData({...formData, item_description: e.target.value})} /></div>
                </div>
              )}
              {modalType === 'testimonial' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Reviewer Name</label><input type="text" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.reviewer_name || ''} onChange={e => setFormData({...formData, reviewer_name: e.target.value})} /></div>
                    <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Title / Location</label><input type="text" className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.reviewer_title || ''} onChange={e => setFormData({...formData, reviewer_title: e.target.value})} /></div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Profile Photo URL</label>
                    <div className="flex gap-2">
                      <input 
                        type="url" 
                        className="flex-grow p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" 
                        value={formData.profile_photo_url || ''} 
                        onChange={e => setFormData({...formData, profile_photo_url: e.target.value})} 
                        placeholder="https://example.com/photo.jpg"
                      />
                      <button 
                        type="button"
                        onClick={() => testimonialFileInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="px-4 py-2 bg-brand-olive text-white rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-brand-olive/90 disabled:opacity-50 transition-all"
                      >
                        {uploadingPhoto ? <FiLoader className="animate-spin" /> : <FiUpload />}
                        {uploadingPhoto ? 'Uploading...' : 'Upload'}
                      </button>
                      <input 
                        type="file" 
                        ref={testimonialFileInputRef} 
                        className="hidden" 
                        accept="image/*" 
                        onChange={handleFileUpload} 
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5"><label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Review Text</label><textarea required rows="4" className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.review_text || ''} onChange={e => setFormData({...formData, review_text: e.target.value})} /></div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Rating</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(num => (
                          <button 
                            key={num}
                            type="button"
                            onClick={() => setFormData({...formData, rating: num})}
                            className={`p-2 rounded-lg border transition-all ${formData.rating === num ? 'bg-brand-olive text-white border-brand-olive' : 'bg-brand-cream/5 border-brand-cream hover:border-brand-olive/20'}`}
                          >
                            <SafeIcon icon={FiStar} className={formData.rating >= num ? 'fill-current' : ''} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Display Order</label>
                      <input type="number" className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.display_order || 0} onChange={e => setFormData({...formData, display_order: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6 items-center">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Status</label>
                      <select className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.status || 'published'} onChange={e => setFormData({...formData, status: e.target.value})}>
                        <option value="published">Published</option>
                        <option value="inactive">Inactive</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3 pt-4">
                      <input 
                        type="checkbox" 
                        id="is_featured"
                        className="w-5 h-5 rounded border-brand-olive/20 text-brand-olive focus:ring-brand-olive"
                        checked={!!formData.is_featured}
                        onChange={e => setFormData({...formData, is_featured: e.target.checked})}
                      />
                      <label htmlFor="is_featured" className="text-[10px] font-black uppercase opacity-40 tracking-widest cursor-pointer">Feature on Home</label>
                    </div>
                  </div>
                </div>
              )}
              {modalType === 'business_profile' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Business Name</label>
                    <input type="text" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.business_name || ''} onChange={e => setFormData({...formData, business_name: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Email</label>
                      <input type="email" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Phone</label>
                      <input type="text" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Address</label>
                    <textarea rows="3" required className="w-full p-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} />
                  </div>
                </div>
              )}
              {modalType === 'rate_setting' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase opacity-40 tracking-widest">Nightly Rate (PHP)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-olive/40 font-bold">₱</span>
                      <input type="number" required className="w-full pl-8 pr-4 py-3 border border-brand-cream outline-none focus:border-brand-olive bg-brand-cream/5 text-sm rounded-lg" value={formData.rate_value || ''} onChange={e => setFormData({...formData, rate_value: e.target.value})} />
                    </div>
                  </div>
                  <p className="text-[10px] text-brand-olive/40 italic">This will update the standard base rate for all direct bookings.</p>
                </div>
              )}
              <div className="flex gap-4 pt-6">
                <button type="submit" disabled={actionLoading} className="btn-primary flex-grow py-3 text-[11px] shadow-lg">{actionLoading ? 'Processing...' : 'Save Changes'}</button>
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-3 text-[10px] font-black uppercase tracking-widest hover:bg-brand-cream rounded-lg transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;